import { useEffect, useState, useMemo } from 'react'
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Table,
  Button,
  Spinner,
  Alert,
  Badge,
  InputGroup,
} from 'react-bootstrap'
import {
  Search,
  Download,
  Check2Square,
  XSquare,
  BoxSeam,
} from 'react-bootstrap-icons'
import ReactPaginate from 'react-paginate'
import * as XLSX from 'xlsx'
import apinode from '../../services/apinode' // ajuste o caminho conforme seu projeto
import Navbar from '../../components/navbar/navbar'

/**
 * Tela de exportação de produtos para a Nuvemshop.
 *
 * Fluxo:
 *  1. Ao entrar na tela, já lista todos os produtos (sem filtro).
 *  2. Filtros de Marca / Descrição / Ativos vão pro backend.
 *     Filtro de Tipo (UN / Kit) é client-side, lendo as opções
 *     direto do array `produtos` já carregado (ver `tiposDisponiveis`).
 *  3. Marca os produtos que quer exportar (vale para a lista filtrada
 *     inteira, não só para a página visível).
 *  4. Botão "Exportar" gera o .xlsx no layout de importação da Nuvemshop.
 *
 * O mapeamento de colunas para a planilha está isolado em
 * `mapParaNuvemshop()` — é o único lugar que você precisa tocar se a
 * Nuvemshop mudar o layout do CSV de importação.
 */

const PREFIXO_IMAGEM = 'https://node.ziphub.com.br/imagens/'
const ITENS_POR_PAGINA_PADRAO = 20

// Cabeçalho oficial da planilha de importação da Nuvemshop (ordem importa).
const CABECALHO_NUVEMSHOP = [
  'Identificador URL',
  'Nome',
  'Categorias',
  'Nome da variação 1',
  'Valor da variação 1',
  'Nome da variação 2',
  'Valor da variação 2',
  'Nome da variação 3',
  'Valor da variação 3',
  'Preço',
  'Preço promocional',
  'Peso (kg)',
  'Altura (cm)',
  'Largura (cm)',
  'Comprimento (cm)',
  'Estoque',
  'SKU',
  'Código de barras',
  'Exibir na loja',
  'Frete gratis',
  'Descrição',
  'Tags',
  'Título para SEO',
  'Descrição para SEO',
  'Marca',
  'Produto Físico',
  'MPN (Cód. Exclusivo Modelo Fabricante)',
  'Sexo',
  'Faixa etária',
  'Custo',
]

// Gera um slug simples a partir da descrição (usado como Identificador URL)
function gerarSlug(texto) {
  return (texto || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

function numeroOuVazio(valor) {
  const n = Number(valor)
  return n && n > 0 ? n.toFixed(2) : ''
}

// Monta a URL completa da foto. Aceita já vir absoluta (http...) ou só o
// nome do arquivo, que é o caso mais comum vindo do banco.
function getUrlImagem(produto) {
  const arquivo = produto.IMG_CAPA || produto.FOTO_PRODUTO
  if (!arquivo) return null
  if (/^https?:\/\//i.test(arquivo)) return arquivo
  return `${PREFIXO_IMAGEM}${arquivo}`
}

function getUnidade(produto) {
  return (produto.UNIDADE || '').toString().trim().toUpperCase()
}

function isKit(produto) {
  return getUnidade(produto) === 'KIT'
}

// Converte uma linha de PRODUTOS (vinda do backend) numa linha no formato
// de colunas da Nuvemshop. Ajuste aqui se a regra de negócio mudar.
function mapParaNuvemshop(produto) {
  const precoVenda = Number(produto.PRECOVENDA) || 0
  const precoAtacado = Number(produto.PRECOATACADO) || 0
  const temPromocao = precoAtacado > 0 && precoAtacado < precoVenda

  return {
    'Identificador URL': gerarSlug(produto.DESCRICAO),
    Nome: produto.DESCRICAO || '',
    Categorias: '',
    'Nome da variação 1': '',
    'Valor da variação 1': '',
    'Nome da variação 2': '',
    'Valor da variação 2': '',
    'Nome da variação 3': '',
    'Valor da variação 3': '',
    Preço: numeroOuVazio(precoVenda),
    'Preço promocional': temPromocao ? numeroOuVazio(precoAtacado) : '',
    'Peso (kg)': numeroOuVazio(produto.PESO_LIQUIDO),
    'Altura (cm)': numeroOuVazio(produto.ALTURA),
    'Largura (cm)': numeroOuVazio(produto.LARGURA),
    'Comprimento (cm)': numeroOuVazio(produto.PROFUNDIDADE),
    Estoque: produto.ESTOQUEATUAL ?? 0,
    SKU: produto.SKU || produto.CODPRODUTO || '',
    'Código de barras': produto.EAN || produto.CODBARRAS || '',
    'Exibir na loja': produto.ATIVO === 'S' ? 'Sim' : 'Não',
    'Frete gratis': 'Não',
    Descrição: produto.DESC_ITEM || produto.DESCRICAO || '',
    Tags: '',
    'Título para SEO': '',
    'Descrição para SEO': '',
    Marca: produto.MARCA_DESC || '',
    'Produto Físico': 'Sim',
    'MPN (Cód. Exclusivo Modelo Fabricante)': produto.CODINTERNO || '',
    Sexo: '',
    'Faixa etária': '',
    Custo: numeroOuVazio(produto.PRECOCUSTO),
  }
}

// Estilos locais, isolados com prefixo `.ns-` para não vazar pro resto do app.
const ESTILOS = `
  .ns-page {
    --ns-accent: #1aa35e;
    --ns-accent-dark: #138349;
    --ns-accent-soft: #eaf7f0;
    background: #f5f6f8;
    min-height: 100vh;
    padding-bottom: 32px;
  }
  .ns-page-title {
    padding: 22px 0 16px;
  }
  .ns-page-title h4 {
    font-weight: 700;
    margin-bottom: 2px;
    color: #1f2430;
  }
  .ns-page-title p {
    color: #8a8f98;
    margin: 0;
    font-size: 0.9rem;
  }
  .ns-card {
    border: 1px solid #ebedf0;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(20, 30, 40, 0.04);
  }
  .ns-card .card-body {
    padding: 1.25rem 1.25rem;
  }
  .ns-filter-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #8a8f98;
    margin-bottom: 5px;
  }
  .ns-btn-buscar {
    background: var(--ns-accent);
    border-color: var(--ns-accent);
    font-weight: 600;
  }
  .ns-btn-buscar:hover,
  .ns-btn-buscar:focus {
    background: var(--ns-accent-dark);
    border-color: var(--ns-accent-dark);
  }
  .ns-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding-bottom: 14px;
    margin-bottom: 4px;
    border-bottom: 1px solid #eef0f2;
  }
  .ns-count-pill {
    background: var(--ns-accent-soft);
    color: var(--ns-accent-dark);
    font-weight: 600;
    border-radius: 999px;
    padding: 5px 14px;
    font-size: 0.82rem;
    white-space: nowrap;
  }
  .ns-table {
    border-collapse: separate;
    border-spacing: 0;
  }
  .ns-table thead th {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #8a8f98;
    font-weight: 600;
    border-bottom: 2px solid #eef0f2;
    white-space: nowrap;
    padding: 12px 10px;
  }
  .ns-table tbody td {
    vertical-align: middle;
    padding: 10px;
  }
  .ns-table tbody tr.is-selected {
    background-color: var(--ns-accent-soft);
  }
  .ns-thumb {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    border: 1px solid #eef0f2;
    background-color: #f1f2f4;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    flex-shrink: 0;
  }
  .ns-sku-chip {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 0.8rem;
    background: #f1f2f4;
    padding: 3px 8px;
    border-radius: 6px;
    color: #555;
    white-space: nowrap;
  }
  .ns-desc-cell {
    max-width: 380px;
  }
  .ns-empty {
    text-align: center;
    padding: 50px 24px;
    color: #8a8f98;
  }
  .ns-empty .ns-empty-icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #f1f2f4;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
    color: #b0b4ba;
  }
  .ns-export-bar {
    position: sticky;
    bottom: 0;
    background: #fff;
    border-top: 1px solid #eef0f2;
    padding: 14px 4px;
    margin-top: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }
  .ns-btn-exportar {
    background: var(--ns-accent);
    border-color: var(--ns-accent);
    font-weight: 600;
  }
  .ns-btn-exportar:hover,
  .ns-btn-exportar:focus {
    background: var(--ns-accent-dark);
    border-color: var(--ns-accent-dark);
  }
  .ns-pagination {
    display: flex;
    list-style: none;
    gap: 4px;
    padding: 0;
    margin: 0;
    align-items: center;
  }
  .ns-pagination li a {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    border-radius: 6px;
    color: #555;
    font-size: 0.85rem;
    cursor: pointer;
    border: 1px solid transparent;
  }
  .ns-pagination li a:hover {
    background: #f1f2f4;
  }
  .ns-pagination li.selected a {
    background: var(--ns-accent);
    color: #fff;
  }
  .ns-pagination li.disabled a {
    color: #c2c6cc;
    cursor: not-allowed;
  }
`

const ExportarNuvemshop = () => {
  const [marcas, setMarcas] = useState([])
  const [marcaSelecionada, setMarcaSelecionada] = useState('')
  const [busca, setBusca] = useState('')
  const [somenteAtivos, setSomenteAtivos] = useState(false)
  const [tipoSelecionado, setTipoSelecionado] = useState('') // '' = todos

  const [produtos, setProdutos] = useState([])
  const [selecionados, setSelecionados] = useState(new Set())

  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState(null)

  const [paginaAtual, setPaginaAtual] = useState(0)
  const [itensPorPagina, setItensPorPagina] = useState(ITENS_POR_PAGINA_PADRAO)

  const conta = localStorage.idconta

  const buscarProdutos = async (filtros = {}) => {
    setLoading(true)
    setErro(null)
    try {
      const params = {}
      if (filtros.marca) params.marca = filtros.marca
      if (filtros.busca) params.busca = filtros.busca
      params.somenteAtivos = filtros.somenteAtivos ?? false

      const res = await apinode.get(`/nuvemshop/produtos/exportar-nuvemshop/${conta}`, {
        params,
      })

      setProdutos(res.data)
      setSelecionados(new Set())
      setPaginaAtual(0)
      setTipoSelecionado('')
    } catch (err) {
      console.error(err)
      setErro('Não foi possível buscar os produtos. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  // Carrega marcas e já lista todos os produtos ao entrar na tela
  useEffect(() => {
    apinode
      .get(`/nuvemshop/produtos/marcas/${conta}`)
      .then((res) => setMarcas(res.data))
      .catch((err) => console.error('Erro ao buscar marcas:', err))

    buscarProdutos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conta])

  const handleBuscar = () => {
    buscarProdutos({ marca: marcaSelecionada, busca, somenteAtivos })
  }

  // Opções do filtro de Tipo, derivadas direto do array já carregado
  // (não bate no backend de novo — só lê o que já está em `produtos`).
  const tiposDisponiveis = useMemo(() => {
    const unicos = new Set(produtos.map((p) => getUnidade(p)).filter(Boolean))
    return Array.from(unicos).sort()
  }, [produtos])

  // Filtro de Tipo aplicado client-side sobre o que já veio do backend
  const produtosFiltrados = useMemo(() => {
    if (!tipoSelecionado) return produtos
    return produtos.filter((p) => getUnidade(p) === tipoSelecionado)
  }, [produtos, tipoSelecionado])

  useEffect(() => {
    setPaginaAtual(0)
  }, [tipoSelecionado])

  const toggleSelecionado = (codproduto) => {
    setSelecionados((prev) => {
      const novo = new Set(prev)
      novo.has(codproduto) ? novo.delete(codproduto) : novo.add(codproduto)
      return novo
    })
  }

  const todosSelecionados =
    produtosFiltrados.length > 0 &&
    produtosFiltrados.every((p) => selecionados.has(p.CODPRODUTO))

  const alternarSelecionarTodos = () => {
    setSelecionados((prev) => {
      const novo = new Set(prev)
      if (todosSelecionados) {
        produtosFiltrados.forEach((p) => novo.delete(p.CODPRODUTO))
      } else {
        produtosFiltrados.forEach((p) => novo.add(p.CODPRODUTO))
      }
      return novo
    })
  }

  const produtosSelecionados = useMemo(
    () => produtos.filter((p) => selecionados.has(p.CODPRODUTO)),
    [produtos, selecionados],
  )

  // Paginação client-side sobre o resultado já filtrado (backend + tipo)
  const totalPaginas = Math.max(1, Math.ceil(produtosFiltrados.length / itensPorPagina))
  const produtosDaPagina = useMemo(
    () =>
      produtosFiltrados.slice(
        paginaAtual * itensPorPagina,
        paginaAtual * itensPorPagina + itensPorPagina,
      ),
    [produtosFiltrados, paginaAtual, itensPorPagina],
  )

  const exportarXlsx = () => {
    if (produtosSelecionados.length === 0) return

    const linhas = produtosSelecionados.map(mapParaNuvemshop)

    const ws = XLSX.utils.json_to_sheet(linhas, { header: CABECALHO_NUVEMSHOP })
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Produtos')

    const dataFormatada = new Date().toISOString().slice(0, 10)
    XLSX.writeFile(wb, `nuvemshop-importacao-${dataFormatada}.xlsx`)
  }

  return (
    <>
      <style>{ESTILOS}</style>
      <Navbar />
      <div className="ns-page">
        <Container>
          <div className="ns-page-title">
            <h4>Exportar para a Nuvemshop</h4>
            <p>Filtre, escolha os produtos e gere a planilha de importação.</p>
          </div>

          <Card className="ns-card mb-3">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={3}>
                  <div className="ns-filter-label">MARCA</div>
                  <Form.Select
                    value={marcaSelecionada}
                    onChange={(e) => setMarcaSelecionada(e.target.value)}
                  >
                    <option value="">Todas as marcas</option>
                    {marcas.map((m) => (
                      <option key={m.ID_MARCA} value={m.ID_MARCA}>
                        {m.DESC_MARCA}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <div className="ns-filter-label">BUSCAR POR DESCRIÇÃO</div>
                  <InputGroup>
                    <InputGroup.Text className="bg-white">
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Ex: spot orientável"
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
                    />
                  </InputGroup>
                </Col>

                <Col md={2}>
                  <div className="ns-filter-label">TIPO</div>
                  <Form.Select
                    value={tipoSelecionado}
                    onChange={(e) => setTipoSelecionado(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {tiposDisponiveis.map((tipo) => (
                      <option key={tipo} value={tipo}>
                        {tipo === 'KIT' ? 'Kit' : tipo}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={2} className="pb-2">
                  <Form.Check
                    type="checkbox"
                    id="somente-ativos"
                    label="Só ativos"
                    checked={somenteAtivos}
                    onChange={(e) => setSomenteAtivos(e.target.checked)}
                  />
                </Col>

                <Col md={2}>
                  <Button
                    className="w-100 ns-btn-buscar"
                    onClick={handleBuscar}
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <>
                        <Search className="me-1" size={14} />
                        Buscar
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {erro && <Alert variant="danger">{erro}</Alert>}

          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#1aa35e' }} />
              <p className="text-muted mt-3 mb-0">Buscando produtos...</p>
            </div>
          )}

          {!loading && produtosFiltrados.length === 0 && !erro && (
            <Card className="ns-card">
              <Card.Body>
                <div className="ns-empty">
                  <div className="ns-empty-icon">
                    <BoxSeam size={24} />
                  </div>
                  <h6 className="mb-1">Nenhum produto encontrado</h6>
                  <p className="mb-0">Ajuste os filtros e busque novamente.</p>
                </div>
              </Card.Body>
            </Card>
          )}

          {!loading && produtosFiltrados.length > 0 && (
            <Card className="ns-card">
              <Card.Body>
                <div className="ns-toolbar">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={alternarSelecionarTodos}
                    >
                      {todosSelecionados ? (
                        <>
                          <XSquare className="me-1" /> Limpar seleção
                        </>
                      ) : (
                        <>
                          <Check2Square className="me-1" /> Selecionar todos
                        </>
                      )}
                    </Button>
                    <span className="text-muted small">
                      {produtosFiltrados.length} produto
                      {produtosFiltrados.length !== 1 && 's'} encontrado
                      {produtosFiltrados.length !== 1 && 's'}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <Form.Select
                      size="sm"
                      style={{ width: 130 }}
                      value={itensPorPagina}
                      onChange={(e) => {
                        setItensPorPagina(Number(e.target.value))
                        setPaginaAtual(0)
                      }}
                    >
                      <option value={20}>20 por página</option>
                      <option value={50}>50 por página</option>
                      <option value={100}>100 por página</option>
                    </Form.Select>

                    <span className="ns-count-pill">
                      {selecionados.size} selecionado{selecionados.size !== 1 && 's'}
                    </span>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table hover className="ns-table mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>
                          <Form.Check
                            type="checkbox"
                            checked={todosSelecionados}
                            onChange={alternarSelecionarTodos}
                          />
                        </th>
                        <th style={{ width: 64 }}>Foto</th>
                        <th>SKU</th>
                        <th>Descrição</th>
                        <th>Tipo</th>
                        <th>Marca</th>
                        <th className="text-end">Preço</th>
                        <th className="text-end">Estoque</th>
                        <th>Ativo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {produtosDaPagina.map((p) => {
                        const selecionado = selecionados.has(p.CODPRODUTO)
                        const urlImagem = getUrlImagem(p)
                        return (
                          <tr
                            key={p.CODPRODUTO}
                            className={selecionado ? 'is-selected' : ''}
                            onClick={() => toggleSelecionado(p.CODPRODUTO)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <Form.Check
                                type="checkbox"
                                checked={selecionado}
                                onChange={() => toggleSelecionado(p.CODPRODUTO)}
                              />
                            </td>
                            <td>
                              <div
                                className="ns-thumb"
                                style={
                                  urlImagem
                                    ? { backgroundImage: `url(${urlImagem})` }
                                    : undefined
                                }
                              />
                            </td>
                            <td>
                              <span className="ns-sku-chip">{p.SKU}</span>
                            </td>
                            <td className="ns-desc-cell">{p.DESCRICAO}</td>
                            <td>
                              {isKit(p) ? (
                                <Badge bg="warning" text="dark">
                                  Kit
                                </Badge>
                              ) : (
                                <span className="text-muted small">
                                  {p.UNIDADE || '—'}
                                </span>
                              )}
                            </td>
                            <td className="text-muted">{p.MARCA_DESC || '—'}</td>
                            <td className="text-end">
                              {Number(p.PRECOVENDA || 0).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td className="text-end">{p.ESTOQUEATUAL ?? 0}</td>
                            <td>
                              <Badge
                                bg={p.ATIVO === 'S' ? 'success' : 'secondary'}
                                style={{ fontWeight: 500 }}
                              >
                                {p.ATIVO === 'S' ? 'Sim' : 'Não'}
                              </Badge>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <span className="text-muted small">
                    Página {paginaAtual + 1} de {totalPaginas}
                  </span>

                  <ReactPaginate
                    pageCount={totalPaginas}
                    forcePage={paginaAtual}
                    onPageChange={({ selected }) => setPaginaAtual(selected)}
                    previousLabel="‹"
                    nextLabel="›"
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={3}
                    containerClassName="ns-pagination"
                    activeClassName="selected"
                    disabledClassName="disabled"
                  />
                </div>

                {selecionados.size > 0 && (
                  <div className="ns-export-bar">
                    <span className="text-muted small">
                      {selecionados.size} produto{selecionados.size !== 1 && 's'} pronto
                      {selecionados.size !== 1 && 's'} para exportar
                    </span>
                    <Button className="ns-btn-exportar" onClick={exportarXlsx}>
                      <Download className="me-2" size={15} />
                      Exportar selecionados (.xlsx)
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
    </>
  )
}

export default ExportarNuvemshop